/**
 * WordPress dependencies
 */
import { store } from '@wordpress/interactivity';
import L from 'leaflet';

// Fonction utilitaire pour nettoyer le nom du camping (enlever "lire la suite :" etc.)
const cleanCampingName = (name) => {
    if (!name) return '';
    return name
        .toString()
        .trim()
        // Enlever "lire la suite :" et ses variations au début
        .replace(/^lire\s+la\s+suite\s*:?\s*/i, '')
        .replace(/^voir\s+(le|la|les)?\s*/i, '')
        .replace(/^-\s*/i, '')
        .trim();
};

const { state } = store('gt/ctv', {
    state: {
        searchQuery: '',
        showSearchResults: false,
        showCampingsList: false,
    },
    actions: {
        // Initialiser les événements pour fermer la liste au clic extérieur
        initSearchDropdown: () => {
            // Fermer la liste au clic en dehors
            const handleClickOutside = (e) => {
                const wrapper = document.querySelector('.gt-ws-ctv-campings-search-wrapper');
                if (wrapper && !wrapper.contains(e.target)) {
                    state.showCampingsList = false;
                }
            };
            
            // Attendre que le DOM soit prêt
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => {
                    document.addEventListener('click', handleClickOutside);
                });
            } else {
                document.addEventListener('click', handleClickOutside);
            }
            
            // Ajouter un événement focus sur l'input pour afficher la liste
            const searchInput = document.querySelector('.gt-ws-ctv-campings-search__input');
            if (searchInput) {
                searchInput.addEventListener('focus', () => {
                    state.showCampingsList = true;
                    setTimeout(() => {
                        if (store('gt/ctv')?.actions?.updateCampingsList) {
                            store('gt/ctv').actions.updateCampingsList();
                        }
                    }, 100);
                });
            }
            
            // Ajouter un gestionnaire de clic sur le conteneur de la liste pour gérer les clics sur les éléments générés dynamiquement
            const dropdownList = document.querySelector('.gt-ws-ctv-campings-search__dropdown-list');
            if (dropdownList) {
                dropdownList.addEventListener('click', (e) => {
                    const button = e.target.closest('.gt-ws-ctv-campings-search__dropdown-item');
                    if (button) {
                        e.preventDefault();
                        e.stopPropagation();
                        if (store('gt/ctv')?.actions?.selectCamping) {
                            store('gt/ctv').actions.selectCamping(e);
                        }
                    }
                });
            }
        },
        
        // Fonction pour afficher la liste des campings
        showCampingsList: () => {
            state.showCampingsList = true;
            // Mettre à jour immédiatement la liste
            if (store('gt/ctv')?.actions?.updateCampingsList) {
                // Attendre un peu pour s'assurer que le DOM est prêt
                setTimeout(() => {
                    store('gt/ctv').actions.updateCampingsList();
                }, 50);
            }
        },
        
        // Fonction pour mettre à jour la liste des campings affichés
        updateCampingsList: () => {
            const dropdownList = document.querySelector('.gt-ws-ctv-campings-search__dropdown-list');
            if (!dropdownList) return;
            
            // S'assurer que le gestionnaire de clic est attaché
            if (!dropdownList.hasAttribute('data-click-handler-attached')) {
                dropdownList.setAttribute('data-click-handler-attached', 'true');
                dropdownList.addEventListener('click', (e) => {
                    const button = e.target.closest('.gt-ws-ctv-campings-search__dropdown-item');
                    if (button) {
                        e.preventDefault();
                        e.stopPropagation();
                        if (store('gt/ctv')?.actions?.selectCamping) {
                            store('gt/ctv').actions.selectCamping(e);
                        }
                    }
                });
            }
            
            // Récupérer les campings depuis le state ou depuis le DOM
            let campings = state.campings || [];
            
            // Si pas de campings dans le state, essayer de les récupérer depuis le DOM
            if (!campings || campings.length === 0) {
                campings = [];
                // Récupérer les campings depuis les éléments de la liste
                const campingElements = document.querySelectorAll('.wp-block-query .wp-block-post, .wp-block-query .camping, [data-gt-camping]');
                campingElements.forEach(campingEl => {
                    const campingId = campingEl.getAttribute('data-gt-camping') || 
                                     campingEl.getAttribute('data-gt-camping-id') ||
                                     campingEl.getAttribute('data-post-id') ||
                                     campingEl.className.match(/post-(\d+)/)?.[1];
                    
                    if (campingId) {
                        // Récupérer le nom depuis le DOM - essayer plusieurs sélecteurs
                        let nameEl = campingEl.querySelector('.wp-block-camping-name, .camping-name');
                        if (!nameEl) {
                            nameEl = campingEl.querySelector('h2, h3, h4, .entry-title, .post-title');
                        }
                        if (!nameEl) {
                            nameEl = campingEl.querySelector('a[href*="camping"]');
                        }
                        let name = nameEl ? nameEl.textContent.trim() : campingEl.textContent.trim().substring(0, 50);
                        // Nettoyer le nom pour enlever "lire la suite :" etc.
                        name = cleanCampingName(name);
                        
                        if (name && name.length > 0) {
                            // Essayer de récupérer la ville et la région depuis le DOM
                            const cityEl = campingEl.querySelector('.wp-block-camping-adresse, .camping-city, [data-city]');
                            const city = cityEl ? cityEl.textContent.trim() : '';
                            
                            const regionEl = campingEl.querySelector('[data-region]');
                            const region = regionEl ? regionEl.getAttribute('data-region') : '';
                            
                            campings.push({
                                idSite: campingId,
                                name: name,
                                city: city,
                                region: region,
                                department: ''
                            });
                        }
                    }
                });
            }
            
            if (!campings || campings.length === 0) {
                dropdownList.innerHTML = '<div class="gt-ws-ctv-campings-search__dropdown-empty">Aucun camping disponible</div>';
                return;
            }
            
            const normalize = (str) => (str || '')
                .toString()
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/\p{Diacritic}/gu, '');
            
            const searchQuery = normalize(state.searchQuery || '');
            
            // Filtrer les campings selon la recherche
            let campingsToShow = campings;
            if (searchQuery.length > 0) {
                campingsToShow = campings.filter(camping => {
                    const name = normalize(camping.name || '');
                    const city = normalize(camping.city || '');
                    const region = normalize(camping.region || '');
                    const department = normalize(camping.department || '');
                    
                    return name.includes(searchQuery) || 
                           city.includes(searchQuery) || 
                           region.includes(searchQuery) ||
                           department.includes(searchQuery);
                });
            }
            
            // Limiter à 50 résultats pour les performances
            campingsToShow = campingsToShow.slice(0, 50);
            
            // Vider la liste avant de la remplir
            dropdownList.innerHTML = '';
            
            // Générer le HTML de la liste
            if (campingsToShow.length === 0) {
                const emptyDiv = document.createElement('div');
                emptyDiv.className = 'gt-ws-ctv-campings-search__dropdown-empty';
                emptyDiv.textContent = searchQuery.length > 0 ? 
                    'Aucun camping trouvé' : 
                    'Commencez à taper pour rechercher';
                dropdownList.appendChild(emptyDiv);
            } else {
                // Créer les boutons avec des événements JavaScript natifs car data-wp-on--click ne fonctionne pas sur du HTML généré dynamiquement
                campingsToShow.forEach(camping => {
                    let name = camping.name || '';
                    // Nettoyer le nom pour enlever "lire la suite :" etc.
                    name = cleanCampingName(name);
                    const city = camping.city || '';
                    const region = camping.region || '';
                    const location = [city, region].filter(Boolean).join(', ');
                    const campingId = camping.idSite || '';
                    
                    const button = document.createElement('button');
                    button.type = 'button';
                    button.className = 'gt-ws-ctv-campings-search__dropdown-item';
                    button.setAttribute('data-camping-id', campingId);
                    button.setAttribute('data-camping-name', name);
                    button.setAttribute('role', 'option');
                    
                    // Créer le contenu HTML
                    const nameSpan = document.createElement('span');
                    nameSpan.className = 'gt-ws-ctv-campings-search__dropdown-item-name';
                    nameSpan.textContent = name;
                    button.appendChild(nameSpan);
                    
                    if (location) {
                        const locationSpan = document.createElement('span');
                        locationSpan.className = 'gt-ws-ctv-campings-search__dropdown-item-location';
                        locationSpan.textContent = location;
                        button.appendChild(locationSpan);
                    }
                    
                    // Ajouter l'événement click directement sur le bouton
                    button.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        console.log('Clic sur camping dans la liste:', {
                            campingId: campingId,
                            campingName: name,
                            camping: camping
                        });
                        
                        // Appeler directement la fonction de sélection avec les données
                        if (store('gt/ctv')?.actions?.selectCampingById) {
                            store('gt/ctv').actions.selectCampingById(campingId, name, camping);
                        } else if (store('gt/ctv')?.actions?.selectCamping) {
                            // Fallback: utiliser l'événement
                            store('gt/ctv').actions.selectCamping(e);
                        }
                    });
                    
                    dropdownList.appendChild(button);
                });
            }
        },
        
        // Fonction pour filtrer par camping
        filterByCamping: (campingId, campingName) => {
            console.log('filterByCamping appelé avec:', campingId, campingName);
            
            // Si on a un ID spécifique, l'utiliser directement
            let targetIds = [];
            if (campingId) {
                targetIds = [campingId.toString()];
            } else {
                // Sinon, chercher par nom
                const normalize = (str) => (str || '')
                    .toString()
                    .trim()
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/\p{Diacritic}/gu, '');
                
                const normalizedQuery = normalize(campingName);
                
                // Filtrer les campings qui correspondent exactement au nom sélectionné
                const matchingCampings = (state.campings || []).filter(camping => {
                    const name = normalize(camping.name || '');
                    return name === normalizedQuery || name.includes(normalizedQuery);
                });
                
                // Si aucun résultat exact, chercher dans tous les champs
                let finalMatchingCampings = matchingCampings;
                if (finalMatchingCampings.length === 0) {
                    finalMatchingCampings = (state.campings || []).filter(camping => {
                        const name = normalize(camping.name || '');
                        const city = normalize(camping.city || '');
                        const address = normalize(camping.fullAddress || '');
                        const region = normalize(camping.region || '');
                        const department = normalize(camping.department || '');
                        
                        return name.includes(normalizedQuery) || 
                               city.includes(normalizedQuery) || 
                               address.includes(normalizedQuery) ||
                               region.includes(normalizedQuery) ||
                               department.includes(normalizedQuery);
                    });
                }
                
                // Récupérer les IDs des campings correspondants
                targetIds = finalMatchingCampings
                    .map(c => c.idSite?.toString())
                    .filter(Boolean);
            }
            
            console.log('IDs à afficher:', targetIds);
            console.log('State campings:', state.campings?.length, 'campings');
            
            // Mettre à jour le state pour la carte
            state.visibleCampingsIds = targetIds;
            state.showSearchResults = true;
            
            // Réinitialiser le département sélectionné pour permettre l'affichage des marqueurs filtrés
            // Masquer tous les clusters (départements et pins) pour n'afficher que le pin recherché
            if (store('gt/ctv')?.state && store('gt/ctv')?.state.map) {
                const mapState = store('gt/ctv').state;
                mapState.currentDepartment = null;
                mapState.departmentZoomLevel = null;
                mapState.pinClusterZoomLevel = null;
                
                // Masquer tous les clusters de départements
                if (mapState.departmentClusters) {
                    Object.values(mapState.departmentClusters).forEach(cluster => {
                        if (mapState.map.hasLayer(cluster)) {
                            mapState.map.removeLayer(cluster);
                        }
                    });
                }
                
                // Masquer tous les clusters de pins
                if (mapState.pinClusters) {
                    Object.values(mapState.pinClusters).forEach(cluster => {
                        if (mapState.map.hasLayer(cluster)) {
                            mapState.map.removeLayer(cluster);
                        }
                    });
                    mapState.pinClusters = {};
                }
            }
            
            // Méthode 1: Chercher directement par attribut data-gt-camping
            let visibleCount = 0;
            
            if (targetIds.length > 0) {
                // IMPORTANT: D'abord, rendre TOUS les éléments visibles temporairement pour la recherche
                // (la pagination peut les avoir cachés)
                const allElementsForSearch = document.querySelectorAll(
                    '.wp-block-query .wp-block-post, ' +
                    '.wp-block-query .camping, ' +
                    '.wp-block-post-template .wp-block-post, ' +
                    '.wp-block-post-template li, ' +
                    'li.wp-block-post, ' +
                    'li.camping, ' +
                    '[data-gt-camping], ' +
                    'li[data-gt-camping]'
                );
                console.log('Rendre tous les éléments visibles pour la recherche:', allElementsForSearch.length);
                allElementsForSearch.forEach(el => {
                    // Rendre visible temporairement pour pouvoir les rechercher
                    el.style.removeProperty('display');
                    el.style.removeProperty('visibility');
                    el.classList.remove('gt-ws-ctv-hidden');
                });
                
                // Maintenant, cacher TOUS les éléments d'abord - utiliser un sélecteur plus large
                const allElements = document.querySelectorAll(
                    '.wp-block-query .wp-block-post, ' +
                    '.wp-block-query .camping, ' +
                    '.wp-block-post-template .wp-block-post, ' +
                    '.wp-block-post-template li, ' +
                    'li.wp-block-post, ' +
                    'li.camping, ' +
                    '[data-gt-camping], ' +
                    'li[data-gt-camping]'
                );
                console.log('Éléments à cacher:', allElements.length);
                allElements.forEach(el => {
                    el.style.setProperty('display', 'none', 'important');
                    el.style.setProperty('visibility', 'hidden', 'important');
                    el.classList.add('gt-ws-ctv-hidden');
                    el.classList.remove('gt-ws-ctv-visible');
                    // Marquer comme filtré par la recherche
                    el.setAttribute('data-search-filtered', 'true');
                });
                
                // Afficher uniquement ceux qui correspondent
                targetIds.forEach(targetId => {
                    // Chercher par attribut data-gt-camping exact
                    const selector = `[data-gt-camping="${targetId}"]`;
                    const matchingElements = document.querySelectorAll(selector);
                    
                    console.log(`Recherche avec selector: ${selector}, trouvé: ${matchingElements.length} éléments`);
                    
                    matchingElements.forEach(el => {
                        el.style.setProperty('display', 'block', 'important');
                        el.style.setProperty('visibility', 'visible', 'important');
                        el.classList.remove('gt-ws-ctv-hidden');
                        el.classList.add('gt-ws-ctv-visible');
                        el.removeAttribute('data-search-filtered');
                        visibleCount++;
                        console.log('Élément affiché par data-gt-camping:', targetId, el);
                    });
                    
                    // Si pas trouvé, essayer aussi avec data-gt-camping-id
                    if (matchingElements.length === 0) {
                        const selector2 = `[data-gt-camping-id="${targetId}"]`;
                        const matchingElements2 = document.querySelectorAll(selector2);
                        
                        matchingElements2.forEach(el => {
                            el.style.setProperty('display', 'block', 'important');
                            el.style.setProperty('visibility', 'visible', 'important');
                            el.classList.remove('gt-ws-ctv-hidden');
                            el.classList.add('gt-ws-ctv-visible');
                            el.removeAttribute('data-search-filtered');
                            visibleCount++;
                            console.log('Élément affiché par data-gt-camping-id:', targetId, el);
                        });
                    }
                });
            }
            
            // Méthode 2: Si toujours rien trouvé, chercher dans tous les éléments
            if (visibleCount === 0) {
                console.log('Aucun élément trouvé par sélecteur direct, recherche dans tous les éléments...');
                
                // IMPORTANT: Rendre TOUS les éléments visibles temporairement pour la recherche
                // (la pagination peut les avoir cachés)
                const allElementsForSearch = document.querySelectorAll(
                    '.wp-block-query .wp-block-post, ' +
                    '.wp-block-query .camping, ' +
                    '.wp-block-post-template .wp-block-post, ' +
                    '.wp-block-post-template li, ' +
                    'li.wp-block-post, ' +
                    'li.camping'
                );
                console.log('Rendre tous les éléments visibles pour la recherche (Méthode 2):', allElementsForSearch.length);
                allElementsForSearch.forEach(el => {
                    // Rendre visible temporairement pour pouvoir les rechercher
                    el.style.removeProperty('display');
                    el.style.removeProperty('visibility');
                    el.classList.remove('gt-ws-ctv-hidden');
                });
                
                const allCampingElements = document.querySelectorAll(
                    '.wp-block-query .wp-block-post, ' +
                    '.wp-block-query .camping, ' +
                    '.wp-block-post-template .wp-block-post, ' +
                    '.wp-block-post-template li, ' +
                    'li.wp-block-post, ' +
                    'li.camping'
                );
                
                console.log('Total éléments trouvés:', allCampingElements.length);
                
                // Afficher TOUS les IDs trouvés pour déboguer
                if (allCampingElements.length > 0) {
                    const allIds = [];
                    const campingIdsFound = [];
                    allCampingElements.forEach((camping, index) => {
                        const id1 = camping.getAttribute('data-gt-camping');
                        const id2 = camping.getAttribute('data-gt-camping-id');
                        const id3 = camping.getAttribute('data-post-id');
                        const id4 = camping.className.match(/post-(\d+)/)?.[1];
                        
                        // Récupérer le nom du camping depuis le DOM
                        const nameEl = camping.querySelector('.wp-block-camping-name, .camping-name, h2, h3, h4, a');
                        let name = nameEl ? nameEl.textContent.trim() : '';
                        name = cleanCampingName(name);
                        
                        if (id1) campingIdsFound.push(id1);
                        if (id2) campingIdsFound.push(id2);
                        
                        allIds.push({ 
                            index,
                            id1, 
                            id2, 
                            id3, 
                            id4,
                            name,
                            className: camping.className
                        });
                    });
                    
                    console.log('Liste de tous les camping IDs trouvés:', [...new Set(campingIdsFound)]);
                    console.log('Exemples d\'éléments (5 premiers):', allIds.slice(0, 5));
                    console.log('ID recherché:', targetIds[0], 'Type:', typeof targetIds[0]);
                    
                    // Chercher si l'ID existe quelque part
                    const foundId = allIds.find(item => 
                        item.id1 === targetIds[0] || 
                        item.id2 === targetIds[0] || 
                        item.id1 === targetIds[0].toString() || 
                        item.id2 === targetIds[0].toString()
                    );
                    console.log('ID trouvé dans les éléments?', foundId);
                    
                    // Chercher par nom aussi
                    const foundByName = allIds.find(item => {
                        const normalize = (str) => (str || '').toString().trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                        return normalize(item.name) === normalize(campingName);
                    });
                    console.log('Élément trouvé par nom?', foundByName);
                }
                
                // Essayer de trouver le camping dans state.campings pour obtenir le nom exact
                let exactCampingName = campingName;
                let matchingCampingData = null;
                if (campingId && state.campings && Array.isArray(state.campings)) {
                    matchingCampingData = state.campings.find(c => {
                        const cId = c.idSite?.toString() || c.idSite;
                        const targetId = campingId.toString();
                        return cId === targetId || cId === campingId;
                    });
                    console.log('Camping trouvé dans state:', matchingCampingData);
                    console.log('Recherche ID:', campingId, 'Type:', typeof campingId);
                    console.log('Premiers campings dans state:', state.campings.slice(0, 3).map(c => ({ idSite: c.idSite, name: c.name })));
                    
                    if (matchingCampingData && matchingCampingData.name) {
                        exactCampingName = matchingCampingData.name;
                        console.log('✓ Nom exact du camping depuis state:', exactCampingName);
                    } else {
                        console.log('⚠ Camping non trouvé dans state, utilisation du nom original:', campingName);
                    }
                } else {
                    console.log('⚠ state.campings non disponible ou invalide:', { 
                        hasCampings: !!state.campings, 
                        isArray: Array.isArray(state.campings),
                        length: state.campings?.length 
                    });
                }
                
                // D'abord, cacher TOUS les éléments
                console.log('Cachage de tous les', allCampingElements.length, 'éléments...');
                allCampingElements.forEach(camping => {
                    camping.style.setProperty('display', 'none', 'important');
                    camping.style.setProperty('visibility', 'hidden', 'important');
                    camping.classList.add('gt-ws-ctv-hidden');
                    camping.classList.remove('gt-ws-ctv-visible');
                    camping.setAttribute('data-search-filtered', 'true');
                });
                
                // Normaliser le nom recherché
                const normalize = (str) => (str || '').toString().trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                const normalizedSearchName = exactCampingName ? normalize(exactCampingName) : (campingName ? normalize(campingName) : '');
                
                // Ensuite, afficher uniquement ceux qui correspondent
                console.log('=== RECHERCHE PAR NOM ===');
                console.log('Nom recherché:', exactCampingName || campingName);
                console.log('Nom normalisé:', normalizedSearchName);
                console.log('Total éléments à vérifier:', allCampingElements.length);
                
                // Afficher les 5 premiers noms trouvés dans le DOM pour debug
                console.log('Exemples de noms dans le DOM (5 premiers):');
                for (let i = 0; i < Math.min(5, allCampingElements.length); i++) {
                    const camping = allCampingElements[i];
                    let nameEl = camping.querySelector('.wp-block-camping-name, .camping-name');
                    if (!nameEl) nameEl = camping.querySelector('h2, h3, h4');
                    if (!nameEl) nameEl = camping.querySelector('a[href*="camping"]');
                    if (!nameEl) nameEl = camping.querySelector('a, .entry-title, .post-title');
                    let name = nameEl ? nameEl.textContent.trim() : camping.textContent.trim().substring(0, 50);
                    name = cleanCampingName(name);
                    const normalizedName = normalize(name);
                    const matches = normalizedName.includes(normalizedSearchName) || normalizedSearchName.includes(normalizedName);
                    console.log(`  [${i}] "${name.substring(0, 60)}" -> "${normalizedName.substring(0, 60)}" ${matches ? '✓ MATCH' : '✗'}`);
                }
                
                // Si on a un nom normalisé, chercher tous les éléments qui correspondent
                if (normalizedSearchName && normalizedSearchName.length > 0) {
                    let foundCount = 0;
                    allCampingElements.forEach((camping, index) => {
                    const campingElId = camping.getAttribute('data-gt-camping') || 
                                      camping.getAttribute('data-gt-camping-id');
                    const postId = camping.className.match(/post-(\d+)/)?.[1];
                    
                    // Récupérer le nom du camping depuis le DOM - essayer plusieurs sélecteurs
                    let nameEl = camping.querySelector('.wp-block-camping-name, .camping-name');
                    if (!nameEl) {
                        nameEl = camping.querySelector('h2, h3, h4');
                    }
                    if (!nameEl) {
                        nameEl = camping.querySelector('a[href*="camping"]');
                    }
                    if (!nameEl) {
                        // Essayer de trouver le premier lien ou texte principal
                        nameEl = camping.querySelector('a, .entry-title, .post-title');
                    }
                    let campingNameInDom = nameEl ? nameEl.textContent.trim() : '';
                    
                    // Si toujours pas de nom, essayer de récupérer depuis le texte de l'élément
                    if (!campingNameInDom || campingNameInDom.length < 3) {
                        // Prendre les premiers caractères du texte de l'élément
                        const textContent = camping.textContent.trim();
                        campingNameInDom = textContent.substring(0, 100); // Prendre les 100 premiers caractères
                    }
                    
                    // Nettoyer le nom pour enlever "lire la suite :" etc.
                    campingNameInDom = cleanCampingName(campingNameInDom);
                    
                    let shouldShow = false;
                    
                    // Vérifier par camping ID
                    if (campingElId && targetIds.length > 0) {
                        shouldShow = targetIds.some(targetId => {
                            return targetId.toString() === campingElId.toString() ||
                                   targetId === campingElId;
                        });
                        if (shouldShow) {
                            console.log('Élément trouvé par ID:', { campingElId, targetId: targetIds[0], index });
                        }
                    }
                    
                        // Recherche par nom - méthode TRÈS SIMPLIFIÉE
                        const normalizedDomName = normalize(campingNameInDom);
                        const cleanDomName = normalizedDomName
                            .replace(/^lire\s+la\s+suite\s*:?\s*/i, '')
                            .replace(/^voir\s+(le|la|les)?\s*/i, '')
                            .replace(/^\s*-\s*/i, '')
                            .trim();
                        
                        // Comparaison plus précise pour éviter les faux positifs
                        if (normalizedSearchName.length > 0 && !shouldShow) {
                            // Test 1: Inclusion directe (le nom du DOM contient le nom recherché complet)
                            if (cleanDomName.includes(normalizedSearchName)) {
                                shouldShow = true;
                                console.log(`✓ [${index}] Trouvé par inclusion directe: "${campingNameInDom.substring(0, 60)}"`);
                            }
                            // Test 2: Inclusion inversée (le nom recherché contient le nom du DOM) - seulement si le nom du DOM fait au moins 10 caractères
                            else if (cleanDomName.length >= 10 && normalizedSearchName.includes(cleanDomName)) {
                                shouldShow = true;
                                console.log(`✓ [${index}] Trouvé par inclusion inversée: "${campingNameInDom.substring(0, 60)}"`);
                            }
                            // Test 3: Par mots-clés significatifs (exclure les mots trop communs)
                            else {
                                const commonWords = ['camping', 'la', 'le', 'de', 'du', 'des', 'les', 'et', 'a', 'au', 'aux', 'le', 'l'];
                                const searchWords = normalizedSearchName.split(/\s+/).filter(w => w.length >= 3 && !commonWords.includes(w.toLowerCase()));
                                
                                if (searchWords.length > 0) {
                                    const matchingWords = searchWords.filter(word => cleanDomName.includes(word));
                                    // Exiger au moins 2 mots significatifs qui correspondent (sauf si c'est un mot unique très spécifique)
                                    if (matchingWords.length >= Math.min(2, searchWords.length)) {
                                        shouldShow = true;
                                        console.log(`✓ [${index}] Trouvé par mots-clés significatifs: "${campingNameInDom.substring(0, 60)}" (mots: ${matchingWords.join(', ')})`);
                                    }
                                }
                            }
                            // Test 4: Si le nom recherché contient "flower", chercher aussi ce mot spécifique
                            // MAIS exiger au moins 2 mots significatifs en commun (pas juste "flower" + "camping")
                            if (!shouldShow && normalizedSearchName.includes('flower')) {
                                if (cleanDomName.includes('flower')) {
                                    // Exclure "camping" et autres mots communs
                                    const commonWords2 = ['camping', 'la', 'le', 'de', 'du', 'des', 'les', 'et', 'a', 'au', 'aux', 'le', 'l'];
                                    const otherWords = normalizedSearchName.split(/\s+/).filter(w => {
                                        const wLower = w.toLowerCase();
                                        return w.length >= 3 && wLower !== 'flower' && !commonWords2.includes(wLower);
                                    });
                                    const matchingOtherWords = otherWords.filter(word => cleanDomName.includes(word));
                                    // Exiger au moins 2 mots significatifs en commun (pas juste "flower" + "camping")
                                    // Pour "Flower Camping La Samaritaine" : otherWords = ["samaritaine"] (1 mot), donc ne devrait pas passer
                                    // Pour "Flower Camping LA STENIOLE" : otherWords = ["steniole"] (1 mot), donc ne devrait pas passer
                                    if (matchingOtherWords.length >= 2) {
                                        shouldShow = true;
                                        console.log(`✓ [${index}] Trouvé par "flower" + autres mots: "${campingNameInDom.substring(0, 60)}" (mots: ${matchingOtherWords.join(', ')}, otherWords: ${otherWords.join(', ')})`);
                                    } else {
                                        // Debug: pourquoi n'est-ce pas trouvé ?
                                        if (index === 64) {
                                            console.log(`✗ [${index}] Test 4 échoué pour "${campingNameInDom.substring(0, 60)}": otherWords=${otherWords.join(', ')}, matchingOtherWords=${matchingOtherWords.join(', ')}`);
                                        }
                                    }
                                }
                            }
                        }
                        
                        if (shouldShow) {
                            camping.style.setProperty('display', 'block', 'important');
                            camping.style.setProperty('visibility', 'visible', 'important');
                            camping.classList.remove('gt-ws-ctv-hidden');
                            camping.classList.add('gt-ws-ctv-visible');
                            camping.removeAttribute('data-search-filtered');
                            visibleCount++;
                            foundCount++;
                        }
                    });
                    console.log(`=== RECHERCHE TERMINÉE: ${foundCount} éléments trouvés sur ${allCampingElements.length} ===`);
                } else {
                    console.log('⚠ Pas de nom recherché disponible (normalizedSearchName vide)');
                }
            }
            
            console.log('Éléments visibles après recherche:', visibleCount);
            
            // Si on a filtré, forcer l'affichage de tous les éléments correspondants (ignorer la pagination)
            if (visibleCount > 0) {
                // Désactiver temporairement la pagination pour afficher tous les résultats filtrés
                const allVisibleElements = document.querySelectorAll(
                    '.wp-block-query .wp-block-post.gt-ws-ctv-visible, ' +
                    '.wp-block-query .camping.gt-ws-ctv-visible, ' +
                    '.wp-block-post-template .wp-block-post.gt-ws-ctv-visible, ' +
                    'li.wp-block-post.gt-ws-ctv-visible'
                );
                allVisibleElements.forEach(el => {
                    el.style.setProperty('display', 'block', 'important');
                    el.style.setProperty('visibility', 'visible', 'important');
                });
            }
            
            // Mettre à jour la carte - forcer la mise à jour
            if (store('gt/ctv')?.actions?.updateMap) {
                // Attendre un peu pour s'assurer que le state est mis à jour
                setTimeout(() => {
                    store('gt/ctv').actions.updateMap();
                    
                    // Si on a trouvé un seul camping, zoomer sur son pin
                    if (visibleCount === 1 && targetIds.length === 1) {
                        const campingId = targetIds[0];
                        const mapState = store('gt/ctv').state;
                        const marker = mapState.markers?.[campingId];
                        if (marker && mapState.map) {
                            const position = marker.getLatLng();
                            mapState.map.setView(position, 10, { animate: true, duration: 0.5 });
                            marker.openPopup();
                        }
                    } else if (visibleCount > 1 && targetIds.length > 0) {
                        // Si plusieurs campings trouvés, zoomer pour tous les voir
                        const mapState = store('gt/ctv').state;
                        const positions = targetIds
                            .map(id => {
                                const marker = mapState.markers?.[id];
                                return marker ? marker.getLatLng() : null;
                            })
                            .filter(pos => pos !== null);
                        
                        if (positions.length > 0 && mapState.map) {
                            const bounds = L.latLngBounds(positions);
                            mapState.map.fitBounds(bounds, { padding: [60, 60], maxZoom: 11, animate: true, duration: 0.5 });
                        }
                    }
                }, 100);
            }
        },
        
        // Fonction pour sélectionner un camping par ID (appelée directement)
        selectCampingById: (campingId, campingName, campingData) => {
            console.log('selectCampingById appelé:', { campingId, campingName, campingData });
            
            if (!campingName) {
                console.log('Pas de nom de camping, abandon');
                return;
            }
            
            const searchInput = document.querySelector('.gt-ws-ctv-campings-search__input');
            if (searchInput) {
                // Mettre à jour la valeur du champ
                searchInput.value = campingName;
                state.searchQuery = campingName.toLowerCase();
                
                // Filtrer la liste et la carte
                if (store('gt/ctv')?.actions?.filterByCamping) {
                    console.log('Appel de filterByCamping avec:', campingId, campingName);
                    store('gt/ctv').actions.filterByCamping(campingId, campingName);
                } else {
                    console.log('filterByCamping non disponible');
                }
            } else {
                console.log('Champ de recherche non trouvé');
            }
            
            state.showCampingsList = false;
        },
        
        // Fonction pour sélectionner un camping depuis la liste (fallback)
        selectCamping: (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const button = e.target.closest('.gt-ws-ctv-campings-search__dropdown-item') || 
                          e.target.closest('[data-camping-name]') ||
                          e.target;
            
            if (!button) return;
            
            const campingId = button.getAttribute('data-camping-id') || button.dataset.campingId;
            const campingName = button.getAttribute('data-camping-name') || button.dataset.campingName;
            
            if (!campingName) return;
            
            const searchInput = document.querySelector('.gt-ws-ctv-campings-search__input');
            if (searchInput) {
                searchInput.value = campingName;
                state.searchQuery = campingName.toLowerCase();
                
                // Filtrer la liste et la carte
                if (store('gt/ctv')?.actions?.filterByCamping) {
                    store('gt/ctv').actions.filterByCamping(campingId, campingName);
                }
            }
            
            state.showCampingsList = false;
        },
        
        // Fonction pour effacer la recherche
        clearSearch: () => {
            const searchInput = document.querySelector('.gt-ws-ctv-campings-search__input');
            if (searchInput) {
                searchInput.value = '';
                state.searchQuery = '';
                state.visibleCampingsIds = [];
                state.showSearchResults = false;
                
                console.log('Effacement de la recherche - réaffichage de tous les campings');
                
                // Retirer tous les marqueurs de filtrage et réafficher tous les éléments
                const allElements = document.querySelectorAll(
                    '.wp-block-query .wp-block-post, ' +
                    '.wp-block-query .camping, ' +
                    '.wp-block-post-template .wp-block-post, ' +
                    '.wp-block-post-template li, ' +
                    'li.wp-block-post, ' +
                    'li.camping'
                );
                
                console.log('Éléments à réafficher:', allElements.length);
                
                allElements.forEach(camping => {
                    camping.removeAttribute('data-search-filtered');
                    camping.style.removeProperty('display');
                    camping.style.removeProperty('visibility');
                    camping.classList.remove('gt-ws-ctv-hidden');
                    camping.classList.remove('gt-ws-ctv-pagination-hidden'); // IMPORTANT: Retirer aussi la classe de pagination
                    camping.classList.add('gt-ws-ctv-visible');
                });
                
                // Réinitialiser le département et réafficher les clusters
                if (store('gt/ctv')?.state) {
                    const mapState = store('gt/ctv').state;
                    mapState.currentDepartment = null;
                    mapState.departmentZoomLevel = null;
                    mapState.pinClusterZoomLevel = null;
                }
                
                // Mettre à jour la carte pour réafficher les clusters et tous les marqueurs
                if (store('gt/ctv')?.actions?.updateMap) {
                    setTimeout(() => {
                        store('gt/ctv').actions.updateMap();
                        
                        // Réafficher les clusters de départements
                        const mapState = store('gt/ctv').state;
                        if (mapState.departmentClusters && mapState.map) {
                            Object.values(mapState.departmentClusters).forEach(cluster => {
                                if (!mapState.map.hasLayer(cluster)) {
                                    cluster.addTo(mapState.map);
                                }
                            });
                        }
                        
                        // Réinitialiser le zoom à la vue initiale
                        if (mapState.map && mapState.currentInitialZoom) {
                            const center = [48.5, 6.0]; // Centre approximatif de la région Grand Est
                            mapState.map.setView(center, mapState.currentInitialZoom, { animate: true, duration: 0.5 });
                        }
                    }, 100);
                }
                
                // Réinitialiser la pagination à la page 1
                setTimeout(() => {
                    // Utiliser jQuery si disponible pour réinitialiser la pagination
                    if (typeof jQuery !== 'undefined') {
                        const $list = jQuery('ul.wp-block-post-template, .wp-block-post-template').first();
                        if ($list.length > 0) {
                        const $items = $list.find('li.wp-block-post');
                        const totalItems = $items.length;
                        
                        // PAGINATION SUPPRIMÉE : Afficher TOUS les éléments
                        $items.each(function() {
                            const $item = jQuery(this);
                            // Retirer toutes les classes qui masquent
                            $item.removeClass('gt-ws-ctv-pagination-hidden');
                            $item.removeClass('gt-ws-ctv-hidden');
                            $item.addClass('gt-ws-ctv-visible');
                            $item.show();
                        });
                            
                            // Mettre à jour les boutons de pagination
                            const $pagination = $list.siblings('.gt-ws-ctv-campings-pagination, .wp-block-query-pagination').first();
                            if ($pagination.length === 0) {
                                $pagination = $list.next('.gt-ws-ctv-campings-pagination, .wp-block-query-pagination').first();
                            }
                            if ($pagination.length === 0) {
                                $pagination = $list.parent().find('.gt-ws-ctv-campings-pagination, .wp-block-query-pagination').first();
                            }
                            
                            if ($pagination.length > 0) {
                                const totalPages = Math.ceil(totalItems / perPage);
                                const queryId = $pagination.find('.gt-ws-ctv-pagination-btn').first().data('query-id') || '';
                                
                                // Mettre à jour les boutons
                                const $prevBtn = $pagination.find('.wp-block-query-pagination-previous');
                                $prevBtn.addClass('is-disabled').html('<span aria-label="Précédent"></span>');
                                
                                const $numbers = $pagination.find('.wp-block-query-pagination-numbers');
                                let numbersHtml = '<span class="page-numbers current" aria-current="page">1</span>';
                                for (let i = 2; i <= totalPages; i++) {
                                    numbersHtml += '<button type="button" class="page-numbers gt-ws-ctv-pagination-btn" data-page="' + i + '" data-query-id="' + queryId + '">' + i + '</button>';
                                }
                                $numbers.html(numbersHtml);
                                
                                const $nextBtn = $pagination.find('.wp-block-query-pagination-next');
                                if (totalPages > 1) {
                                    $nextBtn.removeClass('is-disabled').html(
                                        '<button type="button" class="gt-ws-ctv-pagination-btn" data-page="2" data-query-id="' + queryId + '" aria-label="Suivant"></button>'
                                    );
                                } else {
                                    $nextBtn.addClass('is-disabled').html('<span aria-label="Suivant"></span>');
                                }
                            }
                            
                            // Mettre à jour l'URL pour retirer le paramètre de page
                            const url = new URL(window.location);
                            const pageKey = queryId ? 'query-' + queryId + '-page' : 'query-page';
                            url.searchParams.delete(pageKey);
                            window.history.replaceState({}, '', url);
                            
                            console.log('Pagination réinitialisée - Page 1 sur', totalPages, 'pages');
                        }
                    } else {
                        // Fallback: essayer de cliquer sur le bouton page 1
                        const paginationButtons = document.querySelectorAll('.gt-ws-ctv-pagination-btn[data-page="1"]');
                        if (paginationButtons.length > 0) {
                            paginationButtons[0].click();
                        }
                    }
                }, 200);
            }
        },
        
        // Fonction pour afficher/masquer la liste de résultats de recherche
        toggleSearchResults: () => {
            state.showSearchResults = !state.showSearchResults;
        },
        
        // Mettre à jour la liste lors de la recherche
        handleSearch: (e) => {
            const searchQuery = e.target.value.trim().toLowerCase();
            state.searchQuery = searchQuery;
            
            // Mettre à jour la liste déroulante
            if (store('gt/ctv')?.actions?.updateCampingsList) {
                store('gt/ctv').actions.updateCampingsList();
            }
            
            if (searchQuery.length === 0) {
                // Si la recherche est vide, réinitialiser les filtres
                state.visibleCampingsIds = [];
                state.showSearchResults = false;
                
                console.log('Recherche vide - réaffichage de tous les campings');
                
                // Retirer tous les marqueurs de filtrage et réafficher tous les éléments
                const allElements = document.querySelectorAll(
                    '.wp-block-query .wp-block-post, ' +
                    '.wp-block-query .camping, ' +
                    '.wp-block-post-template .wp-block-post, ' +
                    '.wp-block-post-template li, ' +
                    'li.wp-block-post, ' +
                    'li.camping'
                );
                
                allElements.forEach(camping => {
                    camping.removeAttribute('data-search-filtered');
                    camping.style.removeProperty('display');
                    camping.style.removeProperty('visibility');
                    camping.classList.remove('gt-ws-ctv-hidden');
                    camping.classList.remove('gt-ws-ctv-pagination-hidden'); // IMPORTANT: Retirer aussi la classe de pagination
                    camping.classList.add('gt-ws-ctv-visible');
                });
                
                // Mettre à jour la carte pour afficher tous les marqueurs
                if (store('gt/ctv')?.actions?.updateMap) {
                    setTimeout(() => {
                        store('gt/ctv').actions.updateMap();
                    }, 100);
                }
                
                // Réinitialiser la pagination à la page 1
                setTimeout(() => {
                    // Utiliser jQuery si disponible pour réinitialiser la pagination
                    if (typeof jQuery !== 'undefined') {
                        const $list = jQuery('ul.wp-block-post-template, .wp-block-post-template').first();
                        if ($list.length > 0) {
                        const $items = $list.find('li.wp-block-post');
                        const totalItems = $items.length;
                        
                        // PAGINATION SUPPRIMÉE : Afficher TOUS les éléments
                        $items.each(function() {
                            const $item = jQuery(this);
                            // Retirer toutes les classes qui masquent
                            $item.removeClass('gt-ws-ctv-pagination-hidden');
                            $item.removeClass('gt-ws-ctv-hidden');
                            $item.addClass('gt-ws-ctv-visible');
                            $item.show();
                        });
                            
                            // Mettre à jour les boutons de pagination
                            const $pagination = $list.siblings('.gt-ws-ctv-campings-pagination, .wp-block-query-pagination').first();
                            if ($pagination.length === 0) {
                                $pagination = $list.next('.gt-ws-ctv-campings-pagination, .wp-block-query-pagination').first();
                            }
                            if ($pagination.length === 0) {
                                $pagination = $list.parent().find('.gt-ws-ctv-campings-pagination, .wp-block-query-pagination').first();
                            }
                            
                            if ($pagination.length > 0) {
                                const totalPages = Math.ceil(totalItems / perPage);
                                const queryId = $pagination.find('.gt-ws-ctv-pagination-btn').first().data('query-id') || '';
                                
                                // Mettre à jour les boutons
                                const $prevBtn = $pagination.find('.wp-block-query-pagination-previous');
                                $prevBtn.addClass('is-disabled').html('<span aria-label="Précédent"></span>');
                                
                                const $numbers = $pagination.find('.wp-block-query-pagination-numbers');
                                let numbersHtml = '<span class="page-numbers current" aria-current="page">1</span>';
                                for (let i = 2; i <= totalPages; i++) {
                                    numbersHtml += '<button type="button" class="page-numbers gt-ws-ctv-pagination-btn" data-page="' + i + '" data-query-id="' + queryId + '">' + i + '</button>';
                                }
                                $numbers.html(numbersHtml);
                                
                                const $nextBtn = $pagination.find('.wp-block-query-pagination-next');
                                if (totalPages > 1) {
                                    $nextBtn.removeClass('is-disabled').html(
                                        '<button type="button" class="gt-ws-ctv-pagination-btn" data-page="2" data-query-id="' + queryId + '" aria-label="Suivant"></button>'
                                    );
                                } else {
                                    $nextBtn.addClass('is-disabled').html('<span aria-label="Suivant"></span>');
                                }
                            }
                            
                            // Mettre à jour l'URL pour retirer le paramètre de page
                            const url = new URL(window.location);
                            const pageKey = queryId ? 'query-' + queryId + '-page' : 'query-page';
                            url.searchParams.delete(pageKey);
                            window.history.replaceState({}, '', url);
                            
                            console.log('Pagination réinitialisée - Page 1 sur', totalPages, 'pages');
                        }
                    } else {
                        // Fallback: essayer de cliquer sur le bouton page 1
                        const paginationButtons = document.querySelectorAll('.gt-ws-ctv-pagination-btn[data-page="1"]');
                        if (paginationButtons.length > 0) {
                            paginationButtons[0].click();
                        }
                    }
                }, 200);
                
                return;
            }
            
            state.showSearchResults = true;
            
            // Normaliser la chaîne de recherche (enlever les accents)
            const normalize = (str) => (str || '')
                .toString()
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/\p{Diacritic}/gu, '');
            
            const normalizedQuery = normalize(searchQuery);
            
            // Filtrer les campings qui correspondent à la recherche
            const matchingCampings = (state.campings || []).filter(camping => {
                const name = normalize(camping.name || '');
                const city = normalize(camping.city || '');
                const address = normalize(camping.fullAddress || '');
                const region = normalize(camping.region || '');
                const department = normalize(camping.department || '');
                
                return name.includes(normalizedQuery) || 
                       city.includes(normalizedQuery) || 
                       address.includes(normalizedQuery) ||
                       region.includes(normalizedQuery) ||
                       department.includes(normalizedQuery);
            });
            
            // Récupérer les IDs des campings correspondants
            const matchingIds = matchingCampings
                .map(c => c.idSite?.toString())
                .filter(Boolean);
            
            state.visibleCampingsIds = matchingIds;
            
            // Filtrer la liste des campings
            document.querySelectorAll('.wp-block-query .wp-block-post, .wp-block-query .camping').forEach(camping => {
                const campingId = camping.getAttribute('data-gt-camping') || 
                                 camping.getAttribute('data-gt-camping-id') ||
                                 camping.getAttribute('data-post-id') ||
                                 camping.className.match(/post-(\d+)/)?.[1];
                
                if (campingId && matchingIds.includes(campingId.toString())) {
                    camping.style.display = '';
                } else {
                    camping.style.display = 'none';
                }
            });
            
            // Mettre à jour la carte
            if (store('gt/ctv')?.actions?.updateMap) {
                store('gt/ctv').actions.updateMap();
            }
            
            // Réinitialiser la pagination à la page 1
            const paginationButtons = document.querySelectorAll('.gt-ws-ctv-pagination-btn[data-page="1"]');
            if (paginationButtons.length > 0) {
                paginationButtons[0].click();
            }
        },
    }
});

